import { useGetDrupalCustomConfig } from 'src/hooks/useGetDrupalCustomConfig';
import { useGetLnWebsite } from 'src/hooks/useGetLnWebsite';
import { capitalizeWords } from 'src/utils/helpers';

// looks on @country and replaces @country with country
const formatGeoLocationTitle = (unformattedTitle: string, country: string) => {
  return unformattedTitle.replace(/@country/g, capitalizeWords(country));
};

/**
 * Custom hook that retrieves and formats geo-location configuration for market banners.
 *
 * This hook fetches website-specific IP redirect settings based on language and country,
 * then formats the configuration text by replacing @country placeholders with the actual
 * country name.
 *
 * @param params - Configuration parameters
 * @param params.langcode - The language code to determine the appropriate website
 * @param params.country - The country name used for text formatting and website selection
 *
 * @returns An object containing formatted geo-location configuration:
 * - `text`: The main banner text with @country placeholders replaced
 * - `stayText`: Text for the "stay" button
 * - `leaveText`: Text for the "leave" button
 * - `siteUrl`: The URL of the target website
 *
 * If no IP redirect settings are found or country is not provided, returns empty strings
 * for text fields and undefined for siteUrl.
 *
 * @example
 * ```typescript
 * const { text, stayText, leaveText, siteUrl } = useGeoLocationConfig({
 *   langcode: 'en',
 *   country: 'United States'
 * });
 * ```
 */
export const useGeoLocationConfig = ({
  langcode,
  countryCode,
  countryName,
}: {
  langcode: Languages;
  countryCode: string;
  countryName?: string;
}) => {
  const globalSettings = useGetDrupalCustomConfig(langcode)?.ln_seo_ipredirect_settings;
  const lnWebsite = useGetLnWebsite(langcode, countryCode);

  if (!countryName) {
    return { stayText: '', text: '', leaveText: '', siteUrl: '' };
  }

  const { ipredirectSettings: websiteIpRedirectSettings } = lnWebsite;
  const ipredirectSettings = globalSettings;
  if (websiteIpRedirectSettings) {
    Object.keys(websiteIpRedirectSettings).forEach(key => {
      const value = (websiteIpRedirectSettings as any)[key];
      if (value) {
        (ipredirectSettings as any)[key] = value;
      }
    });
  }

  const unformattedProductTitle = ipredirectSettings?.text;

  return {
    text: formatGeoLocationTitle(unformattedProductTitle ?? '', countryName),
    stayText: ipredirectSettings?.bt_stay,
    leaveText: ipredirectSettings?.bt_leave,
    siteUrl: lnWebsite.siteUrl,
  };
};

export type IGeoLocationConfig = ReturnType<typeof useGeoLocationConfig>;
